/* eslint-disable prefer-arrow-callback */
import React, { useState, useEffect } from 'react';
import { find, findIndex } from 'lodash';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  EyeIcon,
  EyeSlashIcon,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@availity/element';

import { AvailityHeaderLogo, Footer, MessageDisplay, SecurePassword } from '@/components';
import { useAppContext } from '@/context';
import { errorMessages } from '@/global';
import { useFRCallback } from '@/hooks';

// this page node typically renders for the 'ForgottenPassword002' stage value
const PasswordReset = ({ ...props }) => {
  const { stepToRender, setCurrentStep, errorCode } = props;
  const {
    queryParams,
    sessionCredentials,
    setMessages,
    addMessage,
    userApiResponse = {},
    invalidPasswords = [],
    setInvalidPasswords,
    passVars,
    setPassVars,
  } = useAppContext();
  const { callbacks = [] } = useFRCallback(stepToRender);

  // initialize form
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      password: '',
    },
  });

  // redundancy check for userId in the password reset email link
  // necessary for new password validation
  const { userid: paramsUserId } = queryParams; // potential userid from query params
  const { userId: contextUserId } = sessionCredentials; // potential userid entered during login
  const { userId: avUserId } = userApiResponse; // potential userid from users api
  const currentUserIdValue = paramsUserId || contextUserId || avUserId;

  const [currentPasswordValue, setCurrentPasswordValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // check for password validation errors and populate errors if needed.
  useEffect(() => {
    if (stepToRender || errorCode) {
      // check if entered password has already been marked invalid
      const isPasswordInInvalidArray = invalidPasswords.indexOf(currentPasswordValue) >= 0;

      if (['160'].indexOf(errorCode) >= 0) {
        setError('password', {
          type: 'isCommonPassword',
          message: 'Common passwords such as "Password123" will not be accepted.',
        });
      }

      if (['105', '161'].indexOf(errorCode) >= 0) {
        setError('password', {
          type: 'isPasswordValid',
          message: errorMessages.password.valid,
        });
      }

      // get the password callback
      const pwdCallback = find(callbacks, (obj) => obj.type?.toLowerCase() === 'validatedcreatepasswordcallback', {});

      if (pwdCallback && !isPasswordInInvalidArray) {
        // if 105 not specified, check for other failed policies
        const { output = [] } = pwdCallback;
        const failedPoliciesObj = find(output, (obj) => obj.name?.toLowerCase() === 'failedpolicies', {});

        // extract the failed policies
        const { value: failedPoliciesValue } = failedPoliciesObj;
        if (failedPoliciesValue?.length > 0) {
          for (const unparsedPolicyObj of failedPoliciesValue) {
            const parsedPolicy = JSON.parse(unparsedPolicyObj) || {};
            const { policyRequirement = '' } = parsedPolicy;
            const formattedPolicy = policyRequirement?.toLowerCase();

            // populate error message
            switch (formattedPolicy) {
              case 'repeated_characters': {
                addMessage(errorMessages.password.repeatingChars);
                break;
              }
              default: {
                addMessage('Oops! Something went wrong when validating your password. Reload the page and try again.');
              }
            }
          }

          setError('password', { type: 'failedPasswordPolicy', message: errorMessages.password.valid });
        }
      }

      // finally add the password to the invalid array if needed
      if (!isPasswordInInvalidArray && errorCode !== '0') {
        setInvalidPasswords([...invalidPasswords, currentPasswordValue]);
      }
    }
  }, [stepToRender, errorCode]);

  // handle setting passVars when userId or password changes
  useEffect(() => {
    const lowercasePassword = currentPasswordValue?.toLowerCase();
    const lowercaseUserId = currentUserIdValue?.toLowerCase() || '';

    const passHas8To15 = /^.{8,15}$/.test(currentPasswordValue); // length
    const passHasNumber = /\d/.test(currentPasswordValue); // contains digit
    const passHasUpperCase = /[A-Z]/.test(currentPasswordValue); // contains Uppercase
    const passHasLowerCase = /[a-z]/.test(currentPasswordValue); // contains Lowercase
    const passHasSpecial = /[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/.test(currentPasswordValue); // contains special char;
    const passDoesntContainSpaces = currentPasswordValue?.length > 0 && /^\S*$/.test(currentPasswordValue); // no spaces in pass

    // NOTE: the business logic has been updated such that the password cannot contain a 5 character sequence within the userId
    // substring match logic
    // iterate over length of userid and check 5 chars at a time until end of userid is reached (length -4)
    let passContainsUserIdString = false;

    for (let uidStartIndex = 0; uidStartIndex < lowercaseUserId.length - 4; uidStartIndex++) {
      const uidEndIndex = uidStartIndex + 5;
      const substringToTest = lowercaseUserId.slice(uidStartIndex, uidEndIndex);

      if (lowercasePassword?.indexOf(substringToTest?.toLowerCase()) >= 0) {
        passContainsUserIdString = true;
      }
    }

    setPassVars({
      ...passVars,
      passHas8To15,
      passHasNumber,
      passHasUpperCase,
      passHasLowerCase,
      passHasSpecial,
      passDoesntContainUserIdString: lowercasePassword?.length > 0 && !passContainsUserIdString,
      passDoesntContainSpaces,
    });
  }, [currentUserIdValue, currentPasswordValue]);

  const onSubmit = (values) => {
    setMessages([]); // clear out error messages on submit
    const output = { ...stepToRender };

    // TODO: flesh out proper logic
    if (values) {
      const { password } = values;

      const newCallbacks = [...callbacks];
      for (const [index, callbackField] of newCallbacks.entries()) {
        const { input } = callbackField;

        const tokenIndex = findIndex(input, (inputObj) => inputObj.name === 'IDToken2');

        newCallbacks[index].input[tokenIndex] = {
          ...newCallbacks[index]?.input[tokenIndex],
          value: password,
        };
      }

      Object.assign(output.payload.callbacks, [...newCallbacks]);

      setCurrentStep({ ...output });
    }
  };

  return (
    <Grid
      id="password-reset-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper variant="elevation" elevation={6} sx={{ width: { xs: '350px', sm: '500px' } }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                <Typography variant="h1" mb={2}>
                  Reset your password.
                </Typography>
                <Box>
                  <TextField
                    id="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            title="password visibility"
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <EyeIcon fontSize="small" /> : <EyeSlashIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    {...register('password', {
                      onChange: (e) => {
                        const { target = {} } = e;
                        const { value } = target;

                        setCurrentPasswordValue(value);
                      },
                      minLength: {
                        value: 8,
                        message: errorMessages.password.valid,
                      },
                      maxLength: {
                        value: 15,
                        message: errorMessages.password.valid,
                      },
                      pattern: {
                        value:
                          /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?!.*\s)(?=.*[!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-])[\w!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~-]{8,15}$/,
                        message: errorMessages.password.valid,
                      },
                      required: 'Enter a valid password.',
                      validate: {
                        pwdDoesntContainUserIdString: (value) => {
                          // NOTE: the business logic has been updated such that the password cannot contain a 5 character sequence within the userId
                          const formattedUserId = currentUserIdValue?.toLowerCase();
                          const formattedValue = value?.toLowerCase();

                          // substring match logic
                          // iterate over length of userid and check 5 chars at a time until end of userid is reached (length -4)
                          let passDoesntContainUserIdString = true;

                          for (let uidStartIndex = 0; uidStartIndex < formattedUserId.length - 4; uidStartIndex++) {
                            const uidEndIndex = uidStartIndex + 5;
                            const substringToTest = formattedUserId.slice(uidStartIndex, uidEndIndex);

                            if (formattedValue?.toLowerCase().indexOf(substringToTest?.toLowerCase()) >= 0) {
                              passDoesntContainUserIdString = false;
                            }
                          }

                          // fail test with false when the userId is blank, since we cant really check if the pass contains it if its not there yet
                          return formattedUserId && !passDoesntContainUserIdString
                            ? errorMessages.password.valid
                            : true;
                        },
                        isNotKnownInvalidPwd: (value) => {
                          // check if password value is found in invalidPasswords array
                          const isInvalid = invalidPasswords.indexOf(value) >= 0;

                          return isInvalid ? errorMessages.password.valid : true;
                        },
                      },
                    })}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                  />
                </Box>
                <MessageDisplay />
                <SecurePassword />
              </CardContent>
              <CardActions>
                <Button color="primary" type="submit" sx={{ width: '100%' }}>
                  Reset Password
                </Button>
              </CardActions>
            </form>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

PasswordReset.propTypes = {
  stepToRender: PropTypes.object.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  errorCode: PropTypes.string,
};

export default PasswordReset;
